// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { onlyUpdateForPropTypes } from '../../node_module_clones/recompose';
import { createSelector, createStructuredSelector } from 'reselect';

// Utils
import { isPlatformIframe } from '../platform/utils/platformDetailsUtils';

// Selectors
import { getCurrentBoardInboxState, getIsCurrentVisibleBoardFetched } from '../element/board/boardSelector';
import {
    getElementFilterSelector,
    hasFilteredInboxElementsSelector,
} from '../element/elementFilter/elementFilterSelector';

// Components
import Inbox from './Inbox';
import InboxClosed from './InboxClosed';

// Actions
import { openInbox, closeInbox } from './inboxActions';
import { deselectAllElements } from '../element/selection/selectionActions';
import { setFocusSection } from '../reducers/focusActions';
import { clearElementFilter } from '../element/elementFilter/elementFilterActions';

// Constants
import { InboxState } from './inboxConstants';
import { WORKSPACE_SECTIONS } from '../workspace/workspaceConstants';
import { getIsAnnotationModeEnabled } from '../element/drawing/annotation/store/annotationSelector';

/* Redux property connectors. */
export const inboxOpenStateSelector = createSelector(
    hasFilteredInboxElementsSelector,
    getIsCurrentVisibleBoardFetched,
    getCurrentBoardInboxState,
    (hasFilteredInboxElements, isCurrentBoardFetched, currentBoardInboxState) =>
        isCurrentBoardFetched && (currentBoardInboxState === InboxState.OPEN || hasFilteredInboxElements)
            ? InboxState.OPEN
            : InboxState.CLOSED,
);

const mapStateToProps = createStructuredSelector({
    inboxState: inboxOpenStateSelector,
    elementFilter: getElementFilterSelector,
    isDrawingModeEnabled: getIsAnnotationModeEnabled,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchCloseInbox: (id) => dispatch(closeInbox({ id })),
    dispatchOpenInbox: (id) => {
        dispatch(openInbox({ id }));
        dispatch(clearElementFilter());
    },
    dispatchSetFocusSection: (section) => dispatch(setFocusSection(section)),
    deselectAll: (event) => dispatch(deselectAllElements()),
});

const InboxContainer = (props) => {
    const {
        isEditable,
        inboxState,
        elementFilter,
        inboxElementIds,
        gridSize,
        currentBoardId,
        deselectAll,
        currentBoard,
        createShortcutFile,
        isDrawingModeEnabled,
        platformDetails,

        dispatchOpenInbox,
        dispatchCloseInbox,
        dispatchSetFocusSection,
    } = props;

    const setFocusOnCanvas = () => {
        dispatchSetFocusSection(WORKSPACE_SECTIONS.CANVAS);
    };

    const setFocusOnInbox = () => {
        dispatchSetFocusSection(WORKSPACE_SECTIONS.INBOX);
    };

    const closeInbox = (event) => {
        event?.stopPropagation();

        if (elementFilter && !elementFilter.isEmpty()) return;

        dispatchCloseInbox(currentBoardId);
        setFocusOnCanvas();
    };

    const openInbox = (event) => {
        event?.stopPropagation();
        dispatchOpenInbox(currentBoardId);
        setFocusOnInbox();
    };

    const isEmpty = inboxElementIds.length === 0;

    if (isDrawingModeEnabled || (isEmpty && isPlatformIframe(platformDetails))) return null;

    const inboxElement =
        inboxState === InboxState.OPEN ? (
            <Inbox
                {...props}
                key={`${currentBoardId}-open`}
                gridSize={gridSize}
                inboxElementIds={inboxElementIds}
                currentBoardId={currentBoardId}
                deselectAll={deselectAll}
                closeInboxHandler={closeInbox}
                setFocusOnInbox={setFocusOnInbox}
            />
        ) : (
            <InboxClosed
                key={`${currentBoardId}-closed`}
                createShortcutFile={createShortcutFile}
                isEditable={isEditable}
                openInboxHandler={openInbox}
                inboxElementIds={inboxElementIds}
                element={currentBoard}
            />
        );

    return inboxElement;
};

InboxContainer.propTypes = {
    currentBoard: PropTypes.object.isRequired,
    inboxElementIds: PropTypes.array.isRequired,
    currentBoardId: PropTypes.string.isRequired,
    inboxState: PropTypes.string,
    elementFilter: PropTypes.object,
    dispatchCloseInbox: PropTypes.func,
    dispatchOpenInbox: PropTypes.func,
    dispatchSetFocusSection: PropTypes.func,
    deselectAll: PropTypes.func,
    gridSize: PropTypes.number,
    isEditable: PropTypes.bool,
    createShortcutFile: PropTypes.func,
    isDrawingModeEnabled: PropTypes.bool,
    saveElementRenderState: PropTypes.func,
    clearPreviousElementRenderState: PropTypes.func,
    platformDetails: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(onlyUpdateForPropTypes(InboxContainer));
